import { Mail, MapPin, Phone, Star } from "lucide-react"
import Image from "next/image"
import type { DokanStore } from "@/services/dokan-service"

interface StoreHeaderInfoProps {
  store: DokanStore
}

const FALLBACK_AVATAR = "/static/img/user/default-user.jpg"

export const StoreHeaderInfo = ({
  store,
}: StoreHeaderInfoProps): React.ReactNode => {
  const hasRating =
    typeof store.ratingAverage === "number" &&
    Number.isFinite(store.ratingAverage) &&
    typeof store.ratingCount === "number" &&
    store.ratingCount > 0

  return (
    <>
      {store.bannerImageUrl ? (
        <div className='relative h-52 w-full overflow-hidden rounded-xl'>
          <Image
            alt={`Banner da loja ${store.name}`}
            className='object-cover'
            fill
            priority
            sizes='(max-width: 1024px) 100vw, 75vw'
            src={store.bannerImageUrl}
          />
          <div className='absolute inset-0 bg-black/25' />
        </div>
      ) : (
        <div className='relative h-52 w-full overflow-hidden rounded-xl bg-linear-to-r from-indigo-700 via-purple-700 to-fuchsia-700'>
          <div className='absolute inset-0 bg-black/20' />
          <div className='relative z-10 flex h-full items-end p-6'>
            <h2 className='font-semibold text-2xl text-white'>{store.name}</h2>
          </div>
        </div>
      )}

      <div className='relative z-20 mx-4 -mt-10 rounded-xl border border-gray-200 bg-white p-4 shadow-sm'>
        <div className='flex flex-wrap items-center gap-4'>
          <div className='relative h-24 w-24 overflow-hidden rounded-lg border border-gray-200 bg-white'>
            <Image
              alt={`Avatar da loja ${store.name}`}
              className='object-cover'
              fill
              sizes='96px'
              src={store.avatarUrl || FALLBACK_AVATAR}
            />
          </div>

          <div className='min-w-0 flex-1'>
            <h1 className='truncate font-semibold text-3xl text-gray-900'>
              {store.name}
            </h1>

            {hasRating && (
              <p className='mt-2 flex items-center gap-2 text-gray-600 text-sm'>
                <Star className='h-4 w-4 fill-yellow-400 text-yellow-400' />
                Nota {store.ratingAverage?.toFixed(2)} de {store.ratingCount}{" "}
                avaliacao(oes)
              </p>
            )}

            <div className='mt-3 space-y-1 text-gray-600 text-sm'>
              {store.phone && (
                <p className='flex items-center gap-2'>
                  <Phone className='h-4 w-4' />
                  <a
                    className='hover:text-primary hover:underline'
                    href={`tel:${store.phone}`}
                  >
                    {store.phone}
                  </a>
                </p>
              )}

              {store.email && (
                <p className='flex items-center gap-2'>
                  <Mail className='h-4 w-4' />
                  <a
                    className='hover:text-primary hover:underline'
                    href={`mailto:${store.email}`}
                  >
                    {store.email}
                  </a>
                </p>
              )}

              {store.addressLabel && (
                <p className='flex items-start gap-2'>
                  <MapPin className='mt-0.5 h-4 w-4 shrink-0' />
                  <span>{store.addressLabel}</span>
                </p>
              )}

              {store.socials.length > 0 && (
                <div className='flex flex-wrap items-center gap-2 pt-1'>
                  {store.socials.map((social) => (
                    <a
                      className='rounded-full border border-gray-200 px-2 py-1 text-xs hover:border-primary hover:text-primary'
                      href={social.url}
                      key={social.key}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {social.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
